// DBM site-wide referral capture (ANN-647 Gap 1).
//
// When a consultant shares /?ref=<cCustNo> and a RETAIL customer buys, the sale must credit the
// consultant. The DBM order writeback (ANN-645) credits ANY order carrying the `dbm.consultant_ref`
// cart attribute. Before this, only the skin-scan flow (assets/faceage-capture.js) captured ?ref, so
// a customer who browsed the GENERAL catalogue and bought was never credited — the capture scripts
// did not run on catalogue / product / cart pages.
//
// This module is loaded from layout/theme.liquid, so it runs on EVERY page. It is the ONE canonical
// place that:
//   - reads ?ref from the URL and validates it (an AccountMate consultant number, 3-12 digits),
//   - parks a valid ref in BOTH a 30-day cookie (last-touch) AND sessionStorage, under the SAME key
//     the skin-scan flow uses (dbm_skin_scan_ref) — so the two paths share ONE value,
//   - stamps dbm.consultant_ref onto the cart via cart/update.js (a MERGE — it leaves the theme's own
//     cart attributes intact), ONCE per session (guarded), whenever a ref is parked.
//
// It exposes window.DBMReferral, which assets/faceage-capture.js delegates to for both the value and
// the stamp — so the skin-scan path and this site-wide path use ONE key, ONE stamp and ONE guard and
// therefore cannot double-stamp or disagree. Cart attributes persist across add-to-cart onto the
// resulting order, so stamping once on page load whenever a ref exists is sufficient; there is no
// need to hook every add-to-cart button.
(function () {
  if (window.DBMReferral) return; // one instance — a second script tag must not reset the guard

  // The last-touch persistence window, in days. Single named constant so widening or narrowing it is
  // a one-line change.
  var REF_COOKIE_MAX_DAYS = 30;

  // The shared storage key. The skin-scan guide/quiz park a ref under this too; keeping it identical
  // is what lets the general-catalogue path and the skin-scan path share ONE ref value.
  var REF_KEY = 'dbm_skin_scan_ref';
  var CART_ATTRIBUTE = 'dbm.consultant_ref';

  // A ref is an AccountMate consultant number. Validate it here rather than trusting whatever reaches
  // the URL: it is persisted and used to attribute — and pay — a sale. Mirrors faCleanRef in
  // assets/faceage-capture.js so the two paths agree on exactly what a valid ref is.
  function cleanRef(value) {
    var raw = value == null ? '' : String(value).trim();
    return /^[0-9]{3,12}$/.test(raw) ? raw : null;
  }

  // --- storage: cookie (30-day, cross-session) + sessionStorage (survives the login bounce) --------
  function readCookie(name) {
    try {
      var parts = ('; ' + document.cookie).split('; ' + name + '=');
      if (parts.length < 2) return null;
      return decodeURIComponent(parts.pop().split(';').shift());
    } catch (e) { return null; }
  }

  function writeCookie(name, value, days) {
    try {
      var expires = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; path=/; expires=' + expires + '; SameSite=Lax';
    } catch (e) { /* cookies blocked; sessionStorage still covers the in-session case */ }
  }

  // Park a valid ref in BOTH stores. Last-touch: a later ?ref simply overwrites, and the 30-day
  // window restarts from the most recent visit.
  function park(ref) {
    try { window.sessionStorage.setItem(REF_KEY, ref); } catch (e) {}
    writeCookie(REF_KEY, ref, REF_COOKIE_MAX_DAYS);
    // Cart on every device (R5): when this link was opened, so a saved cart carries only what is left of the
    // 30 days to the buyer's other device (assets/dbm-cart-sync.js reads it).
    writeCookie(REF_KEY + '_at', String(Date.now()), REF_COOKIE_MAX_DAYS);
  }

  // Read the parked ref: sessionStorage first (this tab, freshest), then the 30-day cookie (a new
  // session where sessionStorage has been cleared but the cookie survives).
  function readParked() {
    var fromSession = null;
    try { fromSession = cleanRef(window.sessionStorage.getItem(REF_KEY)); } catch (e) {}
    if (fromSession) return fromSession;
    return cleanRef(readCookie(REF_KEY));
  }

  // Resolve the ref for THIS page load. The URL always wins (last-touch); otherwise fall back to the
  // parked value. A valid URL ref is parked so it survives a later navigation or a login bounce.
  function resolve() {
    var fromUrl = null;
    try { fromUrl = cleanRef(new URLSearchParams(window.location.search).get('ref')); } catch (e) {}
    if (fromUrl) { park(fromUrl); return fromUrl; }
    return readParked();
  }

  var value = resolve();

  // --- cart stamp: one-shot per session, MERGE so the theme's own cart attributes survive ----------
  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Self-referral: if the logged-in customer IS the referrer, do not credit them for their own
  // purchase. layout/theme.liquid renders their own am_custno into window.__dbmSelfRef when signed
  // in; when it is not exposed this is simply a no-op (the accepted minor edge in the spec).
  function isSelfReferral(ref) {
    try { return !!ref && cleanRef(window.__dbmSelfRef) === ref; } catch (e) { return false; }
  }

  var stamped = false;
  function stamp() {
    if (stamped || !value) return Promise.resolve();
    // Cart on every device: a saved-cart restore owns the cart while window.DBMCartLock is busy (it may
    // itself re-stamp dbm.consultant_ref, R5) — stamp after it. A free cart stamps now, unchanged.
    var lock = window.DBMCartLock;
    if (lock && typeof lock.busy === 'function' && lock.busy()) {
      return new Promise(function (resolve) { lock.whenIdle(function () { resolve(stamp()); }); });
    }
    if (isSelfReferral(value)) { stamped = true; return Promise.resolve(); }
    stamped = true;
    var body = { attributes: {} };
    body.attributes[CART_ATTRIBUTE] = value;
    return fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      // Release the guard on a non-ok response so a later stamp (e.g. from the skin-scan widget) can
      // retry; a genuine success leaves it latched and the attribute already on the cart.
      if (!r || !r.ok) { stamped = false; }
    }).catch(function () { stamped = false; /* a later stamp retries */ });
  }

  window.DBMReferral = {
    KEY: REF_KEY,
    MAX_DAYS: REF_COOKIE_MAX_DAYS,
    clean: cleanRef,
    value: value,
    stamp: stamp,
    decorateJoinLinks: decorateJoinLinks
  };

  // ---- carry the ref THROUGH the opportunity page ------------------------------------------------
  // The consultant's recruiting link now lands on /pages/business-opportunity?ref=NNNNNN, and its
  // "Start your Journey" buttons are hard-coded in templates/page.business-opportunities.json to a bare
  // /pages/become-a-consultant. Liquid cannot read a query parameter, so the ref is written onto those
  // hrefs here.
  //
  // ⚠️ This is a ROBUSTNESS layer, not the mechanism. Attribution already survives the click without it:
  // become-consultant.liquid's resolveRef() falls back to window.DBMReferral.value, which is the parked
  // 30-day cookie / sessionStorage value. What this adds is the case where storage is blocked or cleared
  // between the two pages — then the URL is the only carrier — plus a ref that is visible in the address
  // bar and survives the recruit copying the link to someone else.
  //
  // Two links are deliberately skipped:
  //   * ?onbehalf=1 — a sponsor adding a recruit directly. The sponsor is the signed-in consultant, so a
  //     ref would be meaningless, and login-bar.liquid keys its #183 "hide join CTAs from a consultant"
  //     escape on that exact parameter.
  //   * [data-dbm-not-a-join-cta] — the explicit marker for a link to this page that is not a
  //     self-registration. Same escape list as the #183 hider, kept in step with it on purpose.
  function decorateJoinLinks(root) {
    if (!value) return 0;
    var scope = root || document;
    var links, i, a, href, n = 0;
    try { links = scope.querySelectorAll('a[href*="/pages/become-a-consultant"]'); } catch (e) { return 0; }
    for (i = 0; i < links.length; i++) {
      a = links[i];
      href = a.getAttribute('href') || '';
      if (/[?&]ref=/.test(href)) continue;                       // already carries one — leave it
      if (/[?&]onbehalf=1(?:&|$)/.test(href)) continue;          // sponsor-adds-recruit, not a referral
      if (a.hasAttribute('data-dbm-not-a-join-cta')) continue;   // explicitly not a join CTA
      a.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + 'ref=' + encodeURIComponent(value));
      n++;
    }
    return n;
  }

  // Stamp once on page load whenever a ref exists — cart attributes persist across add-to-cart onto
  // the order, so this is sufficient without hooking every add-to-cart button. Wait for the DOM so a
  // MERGE cannot race the theme's own cart bootstrapping.
  if (value) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { stamp(); decorateJoinLinks(); });
    } else {
      stamp();
      decorateJoinLinks();
    }
  }
})();
